<template>
  <container-page :title="$t('LoginPage.SignUpPage.signUp')">
    <a-form ref="form"
            class="form"
            :model="signup"
            name="basic"
            :wrapper-col="{ span: 24 }"
            autocomplete="off"
            :rules="rules"
            layout="vertical"
            @finish="onFinish"
            @finish-failed="onFinishFailed">
      <a-form-item name="mobile">
        <a-input v-model:value="signup.mobile"
                 size="large"
                 autocomplete="off"
                 :placeholder="$t('LoginPage.SignUpPage.enterMobile')">
          <template #prefix>
            <mobile-outlined />
          </template>
        </a-input>
      </a-form-item>
      <a-form-item name="username">
        <a-input v-model:value="signup.username"
                 size="large"
                 autocomplete="off"
                 :placeholder="$t('LoginPage.SignUpPage.enterUsername')">
          <template #prefix>
            <user-outlined />
          </template>
        </a-input>
      </a-form-item>
      <a-form-item name="password">
        <a-input-password v-model:value="signup.password"
                          size="large"
                          autocomplete="off"
                          :placeholder="$t('LoginPage.SignUpPage.enterPassword')">
          <template #prefix>
            <lock-outlined />
          </template>
        </a-input-password>
      </a-form-item>
      <a-form-item name="password_repeat">
        <a-input-password v-model:value="signup.password_repeat"
                          size="large"
                          autocomplete="off"
                          :placeholder="$t('LoginPage.SignUpPage.repeatPassword')">
          <template #prefix>
            <lock-outlined />
          </template>
        </a-input-password>
      </a-form-item>
      <a-row :gutter="10">
        <a-col :span="16">
          <a-form-item name="vcode">
            <a-input v-model:value="signup.vcode"
                     size="large"
                     autocomplete="off"
                     :placeholder="$t('LoginPage.SignUpPage.enterRightVCode')">
              <template #prefix>
                <barcode-outlined />
              </template>
            </a-input>
          </a-form-item>
        </a-col>
        <a-col :span="8"
               class="vcode">
          <img :src='signup.vcodeGraph'
               :alt="$t('LoginPage.SignUpPage.clickToRefresh')"
               @click='refreshVCodeGraph' />
        </a-col>
      </a-row>
      <a-row :gutter="10">
        <a-col :span="16">
          <a-form-item name="captcha">
            <a-input v-model:value="signup.captcha"
                     size="large"
                     autocomplete="off"
                     :placeholder="$t('LoginPage.SignUpPage.enterCaptcha')">
              <template #prefix>
                <code-outlined />
              </template>
            </a-input>
          </a-form-item>
        </a-col>
        <a-col :span="8"
               class="captcha">
          <count-down-button html-type="submit"
                             :text="$t('LoginPage.SignUpPage.getCaptcha')"
                             :counting-text-post="$t('LoginPage.SignUpPage.retryAfter')"
                             :disabled="!(signup.isMobileValid && signup.isVCodeValid)"
                             size="large"
                             @click="getCaptcha"></count-down-button>
        </a-col>
      </a-row>
      <a-row class="footer">
        <a-button type="primary"
                  html-type="submit"
                  size="large">{{ $t('LoginPage.SignUpPage.signUpButton') }}</a-button>
        <a type="primary"
           size="large"
           @click="toSignin">{{ $t('LoginPage.SignUpPage.signinWithExistingAccount') }}</a>
      </a-row>
    </a-form>
  </container-page>
</template>


<script setup lang="ts">
import { CountDownButton } from '@iota-cn/ui-components'
import { Validator } from '@iota-cn/util-validation'
import { message } from 'ant-design-vue'
import type { Rule } from 'ant-design-vue/lib/form'
import { container } from 'tsyringe'
import { reactive, ref } from 'vue'
import type { Router } from 'vue-router'
import { CORE_AXIOS, CORE_ROUTER, Http } from '../../../../core'
import { SIGNIN_PATH } from '../constants'
import { useLoginStore } from '../DefaultLoginPagePlugin'
import ContainerPage from './ContainerPage.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const userStore = useLoginStore()

const axios = container.resolve<Http>(CORE_AXIOS)

const form = ref()
const toSignin = () => {
  const router = container.resolve<Router>(CORE_ROUTER)
  router.push({ path: SIGNIN_PATH })
}

const signup = reactive({
  username: '',
  password: '',
  password_repeat: '',
  mobile: '',
  vcode: '',
  vcodeGraph: '',
  isVCodeValid: false,
  captcha: '',
  isMobileValid: false
})

const refreshVCodeGraph = async () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios.get(`/v1/api/validations/codes?graph=${Math.random()}`).then((res: any) => {
    signup.vcodeGraph = res
  })
}

refreshVCodeGraph()

const getCaptcha = async () => {
  const mobile = signup.mobile
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios.post(userStore.api?.fetchSmsValidationCode || `/v1/api/validations/smses`, { mobile }).then((res: any) => {
    if (res.status === 403) {
      message.error(t('LoginPage.SignUpPage.captchaRequestFrequent'))
    }
  })
}

const validateMobile = async (_rule: Rule, value: string) => {
  if (Validator.isPhone(value)) {
    return axios.get(`/v1/api/validations/mobiles/validate?mobile=${value}`)
      .then(() => {
        signup.isMobileValid = true
        return Promise.resolve()
      }).catch(() => {
        signup.isMobileValid = false
        return Promise.reject(t('LoginPage.SignUpPage.enterUnusedMobile'))
      })
  } else {
    signup.isMobileValid = false
    return Promise.reject(t('LoginPage.SignUpPage.enterValidMobile'))
  }
}

const validateName = async (_rule: Rule, value: string) => {
  if (Validator.isUserName(value)) {
    return axios.get(`/v1/api/validations/usernames/validate?username=${value}`)
      .then(() => {
        return Promise.resolve()
      }).catch(() => {
        return Promise.reject(t('LoginPage.SignUpPage.enterUnusedUsername'))
      })
  } else {
    return Promise.reject(t('LoginPage.SignUpPage.enterValidUsername'))
  }
}

const validatePass = async (_rule: Rule, value: string) => {
  if (Validator.isPassword(value)) {
    if (value !== signup.password_repeat && signup.password_repeat) {
      form.value.validateFields(['password_repeat'])
    } else {
      form.value.clearValidate('password_repeat')
    }
    return Promise.resolve()
  } else {
    return Promise.reject(t('LoginPage.SignUpPage.enterValidPassword'))
  }
}

const validatePassRepeat = async (_rule: Rule, value: string) => {
  if (Validator.isPassword(value)) {
    if (value !== signup.password) {
      return Promise.reject(t('LoginPage.SignUpPage.passwordMismatch'))
    } else {
      form.value.clearValidate('password')
      return Promise.resolve()
    }
  } else {
    return Promise.reject(t('LoginPage.SignUpPage.enterValidPassword'))
  }
}

const validateVCode = async (_rule: Rule, value: string) => {
  if (value === undefined || value.length < 6) {
    signup.isVCodeValid = false
    return Promise.reject(t('LoginPage.SignUpPage.enterValidVCode'))
  }

  return axios.get(`/v1/api/validations/codes/validate?vcode=${value}`)
    .then(() => {
      signup.isVCodeValid = true
      return Promise.resolve()
    }).catch(() => {
      signup.isVCodeValid = false
      return Promise.reject(t('LoginPage.SignUpPage.enterValidVCode'))
    })
}

const validateCaptcha = async (_rule: Rule, value: string) => {
  if (value === undefined || value.length < 6) {
    return Promise.reject(t('LoginPage.SignUpPage.enterVCode'))
  }
  return axios.get(`${userStore.api?.validateSmsCode || '/v1/api/validations/smses/validate'}?mobile=${signup.mobile}&captcha=${value}`)
    .then(() => {
      return Promise.resolve()
    }).catch(() => {
      return Promise.reject(t('LoginPage.SignUpPage.enterValidVCode'))
    })
}

const rules: Record<string, Rule[]> = {
  username: [{ required: true, validator: validateName, trigger: 'change' }],
  mobile: [{ required: true, validator: validateMobile, trigger: 'change' }],
  vcode: [{ required: true, validator: validateVCode, trigger: 'change' }],
  captcha: [{ required: true, validator: validateCaptcha, trigger: 'change' }],
  password: [{ required: true, validator: validatePass, trigger: 'change' }],
  password_repeat: [{ required: true, validator: validatePassRepeat, trigger: 'change' }],
}

const onFinish = () => {
  const user = {
    username: signup.username,
    password: signup.password,
    captcha: signup.captcha,
    vcode: signup.vcode,
    mobile: signup.mobile
  }
  axios.post('/v1/api/signup', user).then(() => {
    userStore.signin('/v1/api/signin', {
      mobile: signup.mobile,
      username: signup.username,
      password: signup.password
    }, t('LoginPage.SignUpPage.signUpSuccess'))
  }).catch(() => {
    message.error(t('LoginPage.SignUpPage.signUpFailure'))
  })
}

const onFinishFailed = () => {

}
</script>


<style scoped lang="less">
.form {
  width: 100%;

  .captcha,
  .vcode {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 24px;

    img {
      height: 40px;
      cursor: pointer;
      width: 100%;
    }

    button {
      width: 100%;
    }
  }

  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;

    button {
      width: 50%;
    }
  }
}
</style>
